import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DeliveriesTable } from './Components';
import { useSubmitOrderMutation } from './submitOrder.generated';
import { Variants, motion } from 'framer-motion';
import {
  OrderSummaryWithCheckout,
  fToCurrency,
  useParameters,
} from '@web/utils';
import arrowTop from '@web/utils/src/assets/icons/functions/arrow-top.svg';
import plusIcon from '@web/utils/src/assets/icons/functions/plus-icon.svg';
import { useCustomToast } from '@/hooks/useCustomToast';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { addressToOrderAddress } from '@/utils/addressToOrderAddress';
import { clearCart } from '@agrantis/cart/actions/cart';
import { cartToOrderDataInput } from '@agrantis/cart/cartUtils';
import {
  selectCartTaxRates,
  selectComputedCartByKey,
  selectSummaryForCart,
} from '@agrantis/cart/selectors';

const variants: Variants = {
  open: {
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 200,
      damping: 20,
      mass: 1,
    },
  },
  closed: {
    y: '100%',
    transition: {
      duration: 0.3,
    },
  },
};

const CheckoutPage = () => {
  const { t } = useTranslation();
  const [isMobileSummaryOpen, setMobileSummaryOpen] = useState(false);
  const { retailerId } = useParameters<{ retailerId: string }>();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.data);
  const cart = useAppSelector(selectComputedCartByKey(retailerId));
  console.log('cart', cart);
  const cartTotals = useAppSelector(selectSummaryForCart(retailerId));
  const { totalPercentes, taxesByPercentage } = useAppSelector(
    selectCartTaxRates(retailerId),
  );
  const [submitResult, submitOrder] = useSubmitOrderMutation();

  const billingAddress = addressToOrderAddress(
    user?.primaryAddress,
    user?.givenName,
    user?.familyName,
  )!;

  const disableCheckoutButton = useMemo(() => {
    return submitResult.fetching || !cart?.isValid;
  }, [cart?.isValid, submitResult.fetching]);

  const handleCheckout = async () => {
    if (!cart || !cart.isValid) {
      return;
    }

    const orderParams = {
      retailerId: retailerId.toString(),
      customerId: user?.id.toString(),
      orderData: {
        ...cartToOrderDataInput(cart, billingAddress),
        totalNet: cartTotals.totalNet,
      },
    };

    try {
      const result = await submitOrder(orderParams);
      if (result.data) {
        dispatch(
          clearCart({
            cartKey: retailerId,
          }),
        );

        if (result?.error?.message.includes('cannot_send_email')) {
          showToast('error', t('checkout.email_failed'), 5000);
        }

        navigate(`/order/${result.data.createOrder.id}/success`);
      } else {
        showToast('error', t('checkout.error_placing_order'));
      }
    } catch (error) {
      console.log('SubmitResult error', { ...submitResult });
    }
  };

  const MobileSummaryToggle = () => (
    <div className="md:hidden z-99 fixed bottom-0 left-0 w-full bg-[#FDF3CC] p-5-5">
      <div className="flex justify-between p-1-5 text-3-0 font-bd text-primary-black leading-1-5 items-center">
        <p>{t('checkout.total_gross')}</p>
        <p>{fToCurrency(cartTotals.totalGross ?? 0)}</p>
        <img
          src={arrowTop}
          className="w-3-0 h-3-0"
          onClick={() => setMobileSummaryOpen(true)}
        />
      </div>
    </div>
  );

  const MobileSummaryPanel = () => (
    <motion.div
      className="fixed bg-primary-white p-5-0 z-100 top-0 left-0 w-100-vw h-100-vh"
      initial="closed"
      animate={isMobileSummaryOpen ? 'open' : 'closed'}
      variants={variants}
    >
      <div className="flex justify-between mb-5-0">
        <p className="text-2-8 font-bd">{t('checkout.cart')}</p>
        <img
          src={plusIcon}
          className="transform rotate-45 w-4-0 h-4-0"
          onClick={() => setMobileSummaryOpen(false)}
        />
      </div>
      <OrderSummaryWithCheckout
        disabled={disableCheckoutButton}
        taxesByPercentage={taxesByPercentage}
        totalPercentes={totalPercentes}
        handleOrderSubmit={handleCheckout}
        summary={cartTotals}
        t={t}
      />
    </motion.div>
  );

  const OrderSummary = () => (
    <OrderSummaryWithCheckout
      disabled={disableCheckoutButton}
      taxesByPercentage={taxesByPercentage}
      totalPercentes={totalPercentes}
      handleOrderSubmit={handleCheckout}
      summary={cartTotals}
      t={t}
    />
  );

  return (
    <div className="pb-15-0">
      <div className="grid grid-cols-1 md:grid-cols-[3fr_1fR] gap-x-4-0">
        <div className="pt-5-0">{cart && <DeliveriesTable />}</div>
        <div className="w-full mt-5-0 hidden md:block">
          <OrderSummary />
        </div>
      </div>
      <MobileSummaryToggle />
      <MobileSummaryPanel />
    </div>
  );
};

export default CheckoutPage;
